import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
} from "@mui/material";


/* -------- Helpers (aligned with NurseVisitFormPrint) -------- */

const fmt = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "—";
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const fmtDT = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "—";
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  const yyyy = dt.getFullYear();
  let h = dt.getHours();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  const hh = String(h).padStart(2, "0");
  const min = String(dt.getMinutes()).padStart(2, "0");
  return `${mm}/${dd}/${yyyy} ${hh}:${min} ${ampm}`;
};

// Improved Yes/No handling
const yn = (val) => {
  if (val === null || val === undefined) return "—";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  if (typeof val === "number") return val === 1 ? "Yes" : val === 0 ? "No" : String(val);
  const lc = String(val).trim().toLowerCase();
  if (["yes", "y", "true", "1", "present", "positive"].includes(lc)) return "Yes";
  if (["no", "n", "false", "0", "absent", "negative"].includes(lc)) return "No";
  return String(val);
};
const isYes = (val) => yn(val) === "Yes";

const completedStatus = (val) => {
  if (Array.isArray(val)) {
    if (val.includes("Yes")) return "Completed";
    if (val.includes("No")) return "Not Completed";
    return "—";
  }
  if (typeof val === "boolean") return val ? "Completed" : "Not Completed";
  if (typeof val === "string") {
    const lc = val.toLowerCase();
    if (lc === "yes") return "Completed";
    if (lc === "no") return "Not Completed";
  }
  return "—";
};

const VISIT_FOCUS_ORDER = [
  "Pregnancy education",
  "Labor/birth preparation",
  "Postpaartum care",
  "Infant care/safety",
  "Breastfeeding/feeding",
  "Emotional well-being",
  "Developmental milestones",
  "Parenting/attachment",
  "Family relationships",
  "Substance use",
  "Mental health",
  "Birth spacing/contraception",
  "Medical follow-up",
  "Community resources",
];

const STRENGTHS_ORDER = [
  "Engaged in visit",
  "Appropriate bonding",
  "Followed health advice",
  "Open communication",
  "Safe environment",
  "Support system present",
];

const CONCERNS_ORDER = [
  "Missed appointments",
  "Poor prenatal care",
  "Safety issues",
  "Feeding challenges",
  "Signs of depression/anxiety",
  "IPV risk",
  "Developmental concern",
];

const REFERRALS_ORDER = [
  "WIC",
  "OB/GYN / Pediatrician",
  "Mental Health Services",
  "Domestic Violence Services",
  "Housing Assistance",
  "Doula or Breastfeeding Support",
  "Other",
];

const HOOYO_ORDER = ["Doula", "OB/GYN", "Patient Navigator", "Office staff"];

const orderedSelected = (selected, order) => {
  if (!Array.isArray(selected)) return [];
  return order.filter((item) => selected.includes(item));
};

const firstNonEmpty = (...vals) => {
  for (const v of vals) {
    if (v === 0) return 0;
    if (v !== null && v !== undefined) {
      if (typeof v === "string") {
        if (v.trim() !== "") return v;
      } else {
        return v;
      }
    }
  }
  return undefined;
};

const formatListWithOther = (selected, otherText) => {
  if (!Array.isArray(selected) || selected.length === 0) return "—";
  const other = typeof otherText === "string" ? otherText.trim() : "";
  const mapped = selected
    .map((item) => (item === "Other" ? other || "Other" : item))
    .filter(Boolean);
  const cleaned = mapped.filter((s) => !(typeof s === "string" && s.trim() === ""));
  return cleaned.length ? cleaned.join(", ") : "—";
};

const Row = ({ label, value }) => (
  <tr style={{ background: "white" }}>
    <td style={{ width: "30%", padding: "6px", border: "1px solid #ddd", fontWeight: 600 }}>
      {label}
    </td>
    <td style={{ width: "70%", padding: "6px", border: "1px solid #ddd", whiteSpace: "pre-wrap" }}>
      {value ?? "—"}
    </td>
  </tr>
);

const Section = ({ title }) => (
  <tr>
    <td colSpan={2} style={{ padding: "8px", border: "1px solid #ddd", fontWeight: "bold" }}>
      {title}
    </td>
  </tr>
);

const CheckListGrid = ({ items }) => {
  if (!items?.length) return <span>—</span>;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        columnGap: "12px",
        rowGap: "6px",
        pageBreakInside: "avoid",
      }}
    >
      {items.map((item) => (
        <div key={item} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ color: "#0d7f6a", fontWeight: 700 }}>✓</span>
          <span>{item}</span>
        </div>
      ))}
    </div>
  );
};

/* Detect if "Concerns" is selected for incision */
const isConcernsSelected = (v) => {
  if (typeof v === "boolean") return v;
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return s === "concerns" || s.includes("concern");
};

/* ------------- Field resolver/normalizer with variants ------------- */

const getVal = (o, name) => {
  if (!o || !name) return undefined;
  const pascal = name.charAt(0).toUpperCase() + name.slice(1);
  return firstNonEmpty(o[name], o[pascal]);
};
const getAny = (o, ...names) => firstNonEmpty(...names.map((k) => getVal(o, k)));
const getArr = (o, name) => {
  const v = getVal(o, name);
  if (Array.isArray(v)) return v;
  if (typeof v === "string") {
    try {
      const p = JSON.parse(v);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
};

const pickByKeyContains = (obj, mustContain = [], alsoAllow = []) => {
  if (!obj || typeof obj !== "object") return undefined;
  const keys = Object.keys(obj);
  const must = mustContain.map((t) => t.toLowerCase());
  const allow = (alsoAllow || []).map((t) => t.toLowerCase());
  for (const k of keys) {
    const lk = k.toLowerCase();
    if (must.every((t) => lk.includes(t)) && (allow.length === 0 || allow.some((t) => lk.includes(t)))) {
      const v = obj[k];
      if (typeof v === "string" && v.trim() !== "") return v;
    }
  }
  return undefined;
};

const normalizeNurse = (n) => {
  if (!n) return {};
  const root = n?.data && typeof n.data === "object" ? n.data : n;

  // Client-identified goals: add patientGoals/patientGoal and fuzzy match
  const goalsFuzzy =
    pickByKeyContains(root, ["goal"], ["client", "identified"]) ||
    pickByKeyContains(root, ["client", "goal"]) ||
    pickByKeyContains(root, ["goals"]);

  // Prefer the exact nurse signature message
  const nurseMsgExplicit = getAny(root, "messageNurseSign", "messageNurseSignature", "messageNurseVisitSignature", "message");
  const nurseMsgFuzzy =
    pickByKeyContains(root, ["message", "nurse", "sign"]) ||
    pickByKeyContains(root, ["message", "signature"]) ||
    undefined;

  const nurseSigDate = firstNonEmpty(
    getAny(
      root,
      "nurseSignatureDate",
      "NurseSignatureDate",
      "nurseSignatureDATE",
      "nurseDate",
      "nurseSignedDate",
      "rnSignatureDate",
      "providerDate",
      "signatureDate",
      "dateSigned",
      "signedOn",
      "signedDate"
    )
  );

  return {
    // Header
    clientId: firstNonEmpty(
      getAny(root, "clientId", "clientID", "mrn", "MRN", "medicalRecordNumber", "chartNumber", "patientId", "patientID", "clientMRN"),
      getAny(root, "clientUniqueId", "clientNo", "clientNumber")
    ),
    clientName: getAny(root, "clientName"),
    dob: firstNonEmpty(getAny(root, "dob", "dateOfBirth")),
    deliveryDate: getAny(root, "deliveryDate"),
    visitDate: firstNonEmpty(getAny(root, "visitDate", "assessmentDate")),
    visitNumber: firstNonEmpty(getAny(root, "visitNumber", "visitType")),
    visitLocation: getAny(root, "visitLocation"),
    visitLocationOther: getAny(root, "visitLocationOther"),
    interpreterUsed: getAny(root, "interpreterUsed"),
    interpreterLanguage: getAny(root, "interpreterLanguage"),
    attendees: getArr(root, "attendees"),
    attendeesOther: getAny(root, "attendeesOther"),

    visitFocus: getArr(root, "visitFocus"),

    temperature: getAny(root, "temperature"),
    pulse: firstNonEmpty(getAny(root, "pulse", "heartRate")),
    respiratoryRate: firstNonEmpty(getAny(root, "respiratoryRate", "resp")),
    bloodPressure: firstNonEmpty(getAny(root, "bloodPressure", "bp")),
    pain: getAny(root, "pain"),
    weight: getAny(root, "weight"),
    fundalHeight: getAny(root, "fundalHeight"),

    // Edema + location with alternate keys
    edema: getAny(root, "edema", "hasEdema", "edemaPresent", "edemaYesNo", "oedema", "oedemaYesNo"),
    edemaLocation: getAny(root, "edemaLocation", "locationOfEdema", "oedemaLocation", "edemaSite", "edemaArea"),

    generalObservations: getArr(root, "generalObservations"),
    generalObservationsOther: getAny(root, "generalObservationsOther"),
    lochia: getAny(root, "lochia"),

    incision: getAny(root, "incision"),
    incisionConcerns: firstNonEmpty(
      getAny(
        root,
        "incisionConcerns",
        "incisionConcern",
        "incisionConcernDetail",
        "incisionNotes",
        "incisionOther",
        "incisionComment",
        "incisionDetails",
        "incisionDetail",
        "csectionConcern",
        "csectionConcernDetail",
        "cesareanConcern",
        "cesareanConcernDetail"
      ),
      pickByKeyContains(root, ["incision"], [
        "concern",
        "detail",
        "details",
        "note",
        "notes",
        "issue",
        "comment",
        "remark",
        "site",
        "csection",
        "c-section",
        "cesarean",
      ])
    ),

    breastExam: getAny(root, "breastExam"),
    otherFindings: getAny(root, "otherFindings"),
    uterus: getAny(root, "uterus"),
    perineum: getAny(root, "perineum"),

    strengths: getArr(root, "strengths"),
    strengthsOther: getAny(root, "strengthsOther"),
    concerns: getArr(root, "concerns"),
    concernsOther: getAny(root, "concernsOther"),

    edinburghCompleted: getAny(root, "edinburghCompleted"),
    edinburghFindings: getAny(root, "edinburghFindings"),
    developmentalCompleted: getAny(root, "developmentalCompleted"),
    developmentalFindings: getAny(root, "developmentalFindings"),
    safeSleepCompleted: getAny(root, "safeSleepCompleted"),
    safeSleepFindings: getAny(root, "safeSleepFindings"),
    ipvCompleted: getAny(root, "ipvCompleted"),
    ipvFindings: getAny(root, "ipvFindings"),
    feedingCompleted: getAny(root, "feedingCompleted"),
    feedingFindings: getAny(root, "feedingFindings"),
    substanceCompleted: getAny(root, "substanceCompleted"),
    substanceFindings: getAny(root, "substanceFindings"),
    birthSpacingCompleted: getAny(root, "birthSpacingCompleted"),
    birthSpacingFindings: getAny(root, "birthSpacingFindings"),

    educationParentChildInteraction: getAny(root, "educationParentChildInteraction"),
    educationMaternalWellbeing: getAny(root, "educationMaternalWellbeing"),
    educationHealthDevelopment: getAny(root, "educationHealthDevelopment"),
    educationSafetyEnvironment: getAny(root, "educationSafetyEnvironment"),
    educationFamilyCommunitySupport: getAny(root, "educationFamilyCommunitySupport"),

    referrals: getArr(root, "referrals"),
    referralsOther: getAny(root, "referralsOther"),
    hooyoReferral: getArr(root, "hooyoReferral"),

    referralNotes: getAny(root, "referralNotes"),

    // Add patientGoals/patientGoal and fuzzy search
    clientGoals: firstNonEmpty(
      getAny(
        root,
        "clientGoals",
        "clientIdentifiedGoals",
        "clientIdentifiedGoal",
        "clientGoal",
        "goals",
        "goalsAndActions",
        "clientGoalsNotes",
        "goalsNotes",
        "goal",
        "patientGoals",
        "patientGoal"
      ),
      goalsFuzzy
    ),

    nurseRecommendations: getAny(root, "nurseRecommendations"),
    followupPlan: getAny(root, "followupPlan"),
    nextVisitDate: getAny(root, "nextVisitDate"),

    nurseNotes: getAny(root, "nurseNotes"),
    nurseName: firstNonEmpty(getAny(root, "nurseName", "NurseName", "rnName", "providerName", "signedByName", "staffName")),
    nurseUserName: firstNonEmpty(getAny(root, "nurseUserName", "userName", "username", "signedBy", "providerUserName")),

    credential: getAny(root, "credential"),
    dateOfEntry: getAny(root, "dateOfEntry"),

    // Signature message & date (prefer exact message)
    nurseMessage: firstNonEmpty(nurseMsgExplicit, nurseMsgFuzzy),
    nurseSignatureDate: nurseSigDate,

    customFormTitle: getAny(root, "customFormTitle"),
  };
};

/* ------------------------- Component ------------------------- */

const NurseVisitPreview = ({ data }) => {
  const [nurse, setNurse] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const API_BASE_URL =
    process.env.NEXT_PUBLIC_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    "https://neuroez4.devforhealth.com/api";

  const { patientFormId, formTitle, particeName } = data || {};

  const nurseUrl = `${API_BASE_URL}/NurseVisit/getNurseVisitByFormId?PatientFormId=${patientFormId}`;
  const baseFormUrl = `${API_BASE_URL}/Forms/GetPatientFormsbyId?Id=${patientFormId}`;

  useEffect(() => {
    if (!patientFormId) return;
    let active = true;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem('neuro_patientportal_auth_token')
        if (!token) throw new Error("Authentication token missing.");
        const headers = { Authorization: `Bearer ${token}`, Accept: "application/json" };

        const formRes = await fetch(baseFormUrl, { headers });
        if (!formRes.ok) throw new Error(`Base form failed (${formRes.status})`);
        const formRaw = await formRes.json();
        const formData = Array.isArray(formRaw) ? formRaw[0] || {} : formRaw || {};

        const nurseRes = await fetch(nurseUrl, { headers });
        if (!nurseRes.ok) throw new Error(`Nurse Visit failed (${nurseRes.status})`);
        const nurseRaw = await nurseRes.json();
        const nurseData = Array.isArray(nurseRaw) ? nurseRaw[0] || {} : nurseRaw || {};

        if (!active) return;

        setForm(formData);
        setNurse(normalizeNurse(nurseData));
      } catch (e) {
        if (active) setError(e.message || "Failed to load Nurse Visit form.");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [API_BASE_URL, patientFormId]);

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", height: "60vh", alignItems: "center" }}>
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Typography color="error" align="center" sx={{ p: 3 }}>
        {error}
      </Typography>
    );

  if (!nurse)
    return (
      <Typography color="textSecondary" align="center" sx={{ p: 3 }}>
        No data available.
      </Typography>
    );

  const fileNo = firstNonEmpty(form.accountNo) ?? "—";
  const clientName = firstNonEmpty(form.patientName, nurse.clientName, form.clientName) ?? "—";
  const dobVal = fmt(firstNonEmpty(nurse.dob, form.dob, form.dateOfBirth));
  const dosVal = fmt(firstNonEmpty(nurse.visitDate, form.visitDate, form.assessmentDate));

  // Header values (match prenatal/postpartum style)
  const practiceName = firstNonEmpty(particeName, form.practiceName) || "";
  const headerTitle =
    firstNonEmpty(nurse.customFormTitle, form.customFormTitle, formTitle) || "Nurse Visit";

  const clientIdMrn = firstNonEmpty(nurse.clientId, form.patientId, form.PatientID, form.accountNo) ?? "—";

  // Signature (prefer explicit message from either nurse or base form)
  const explicitSignatureMsg = firstNonEmpty(
    nurse.nurseMessage,
    form.messageNurseSign,
    form.messageNurseSignature,
    form.messageNurseVisitSignature,
    form.message
  );

  const signatureDate = firstNonEmpty(
    nurse.nurseSignatureDate,
    form.nurseSignatureDate,
    form.rnSignatureDate,
    form.providerDate,
    form.signedOn,
    form.signedDate,
    form.dateSigned
  );

  const signerDisplay = [
    firstNonEmpty(form.nurseName, nurse.nurseName),
    firstNonEmpty(nurse.nurseUserName, form.nurseUserName),
  ]
    .filter(Boolean)
    .join(", ");

  const signText = explicitSignatureMsg?.trim()
    ? explicitSignatureMsg.trim()
    : (signatureDate || signerDisplay)
      ? `Digitally signed by ${signerDisplay || "—"} on ${fmtDT(signatureDate)}.`
      : "__";

  const visitFocusSelected = orderedSelected(nurse.visitFocus, VISIT_FOCUS_ORDER);
  const strengthsSelected = orderedSelected(nurse.strengths, STRENGTHS_ORDER);
  const concernsSelected = orderedSelected(nurse.concerns, CONCERNS_ORDER);
  const referralsSelected = orderedSelected(nurse.referrals, REFERRALS_ORDER);
  const hooyoSelected = orderedSelected(nurse.hooyoReferral, HOOYO_ORDER);

  const screeners = [
    { label: "Edinburgh Depression Scale", completedKey: "edinburghCompleted", findingsKey: "edinburghFindings" },
    { label: "Developmental Observation", completedKey: "developmentalCompleted", findingsKey: "developmentalFindings" },
    { label: "Safe Sleep Environment", completedKey: "safeSleepCompleted", findingsKey: "safeSleepFindings" },
    { label: "IPV / Safety Screening", completedKey: "ipvCompleted", findingsKey: "ipvFindings" },
    { label: "Feeding/Nutrition Check", completedKey: "feedingCompleted", findingsKey: "feedingFindings" },
    { label: "Substance Use Screening", completedKey: "substanceCompleted", findingsKey: "substanceFindings" },
    { label: "Birth Spacing Plan", completedKey: "birthSpacingCompleted", findingsKey: "birthSpacingFindings" },
  ];

  return (
    <Paper sx={{ p: 4, bgcolor: "#fff", borderRadius: 2, boxShadow: "0 3px 8px rgba(0,0,0,0.08)" }}>
      {/* Header: match Prenatal/Postpartum style (no logo) */}
      <Box
        sx={{
          position: "sticky",
          top: "-16px",
          zIndex: (t) => t.zIndex.appBar,
          bgcolor: "#fff",
          p: 4,
          pb: 0,
          mb: 3,
        }}
      >
        <Typography variant="subtitle2" sx={{ position: "absolute", top: 16, left: 16 }}>
          <strong>DOS:</strong> {dosVal}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            fontWeight: "bold",
            color: "#000",
          }}
        >
          {headerTitle}
        </Typography>
        {/* <Typography variant="h6" sx={{ textAlign: "center", fontWeight: "bold", color: "#000" }}>
          {practiceName}
        </Typography> */}
        <Box sx={{ height: 28 }} />
        <Divider />
      </Box>

      {/* Patient Strip */}
      <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Grid item xs={4}>
          <Typography variant="body2"><strong>File #:</strong> {fileNo}</Typography>
        </Grid>
        <Grid item xs={4} textAlign="center">
          <Typography variant="body2"><strong>Client Name:</strong> {clientName}</Typography>
        </Grid>
        <Grid item xs={4} textAlign="right">
          <Typography variant="body2"><strong>DOB:</strong> {dobVal}</Typography>
        </Grid>
      </Grid>
      <Divider sx={{ mb: 3 }} />

      {/* Body table */}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12pt", background: "white" }}>
        <tbody>
          {/* Client & Visit Information */}
          <Section title="Client & Visit Information" />
          <Row label="Client ID / MRN" value={clientIdMrn} />
          <Row label="Estimated Delivery Date" value={fmt(nurse.deliveryDate)} />
          <Row label="Visit Date" value={fmt(nurse.visitDate)} />
          <Row label="Visit Type" value={nurse.visitNumber} />
          <Row
            label="Visit Location"
            value={
              nurse.visitLocation === "Other"
                ? nurse.visitLocationOther?.trim() || "Other"
                : nurse.visitLocation ?? "—"
            }
          />
          <Row label="Interpreter Used" value={yn(nurse.interpreterUsed)} />
          <Row label="Interpreter Language" value={nurse.interpreterLanguage} />
          <Row label="Attendees Present" value={formatListWithOther(nurse.attendees, nurse.attendeesOther)} />

          {/* Visit Focus */}
          <Section title="Visit Focus" />
          <tr>
            <td colSpan={2} style={{ padding: "8px", border: "1px solid #ddd", background: "white" }}>
              <CheckListGrid items={visitFocusSelected} />
            </td>
          </tr>

          {/* Maternal Physical Assessment */}
          <Section title="Maternal Physical Assessment" />
          <Row label="Temperature (°F)" value={nurse.temperature} />
          <Row label="Pulse (bpm)" value={nurse.pulse} />
          <Row label="Respiratory Rate" value={nurse.respiratoryRate} />
          <Row label="Blood Pressure" value={nurse.bloodPressure} />
          <Row label="Pain (0-10)" value={nurse.pain} />
          <Row label="Weight" value={nurse.weight} />
          <Row label="Fundal Height (cm)" value={nurse.fundalHeight} />
          <Row label="Edema" value={yn(nurse.edema)} />
          {isYes(nurse.edema) && String(nurse.edemaLocation || "").trim() !== "" && (
            <Row label="Edema Location" value={nurse.edemaLocation} />
          )}
          <Row label="General Observations" value={formatListWithOther(nurse.generalObservations, nurse.generalObservationsOther)} />
          <Row label="Lochia Assessment" value={nurse.lochia} />
          <Row label="Incision/C-section Site" value={nurse.incision || "—"} />
          {(isConcernsSelected(nurse.incision) ||
            (typeof nurse.incisionConcerns === "string" && nurse.incisionConcerns.trim() !== "")) && (
              <Row label="Incision Concerns (details)" value={nurse.incisionConcerns || "—"} />
            )}
          <Row label="Breast Exam" value={nurse.breastExam} />
          <Row label="Other Findings" value={nurse.otherFindings} />
          <Row label="Uterus" value={nurse.uterus} />
          <Row label="Perineum" value={nurse.perineum} />

          {/* Client Strengths & Concerns */}
          <Section title="Client Strengths & Concerns" />
          <tr>
            <td colSpan={2} style={{ padding: 0, border: "1px solid #ddd" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  <tr>
                    <td style={{ width: "50%", verticalAlign: "top", padding: "8px" }}>
                      <div style={{ fontWeight: 700, marginBottom: "6px" }}>Strengths Observed</div>
                      <CheckListGrid items={strengthsSelected} />
                      {Array.isArray(nurse.strengths) &&
                        nurse.strengths.includes("Other") &&
                        nurse.strengthsOther?.trim() && (
                          <div style={{ marginTop: "6px" }}>
                            <strong>Other:</strong> {nurse.strengthsOther.trim()}
                          </div>
                        )}
                    </td>
                    <td style={{ width: "50%", verticalAlign: "top", padding: "8px", borderLeft: "1px solid #ddd" }}>
                      <div style={{ fontWeight: 700, marginBottom: "6px" }}>Concerns Identified</div>
                      <CheckListGrid items={concernsSelected} />
                      {Array.isArray(nurse.concerns) &&
                        nurse.concerns.includes("Other") &&
                        nurse.concernsOther?.trim() && (
                          <div style={{ marginTop: "6px" }}>
                            <strong>Other:</strong> {nurse.concernsOther.trim()}
                          </div>
                        )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>

          {/* Assessments & Screenings */}
          <Section title="Assessments & Screenings" />
          <tr>
            <td colSpan={2} style={{ padding: 0, border: "1px solid #ddd" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12pt" }}>
                <thead>
                  <tr>
                    <th style={{ width: "40%", padding: "6px", border: "1px solid #ddd", textAlign: "left" }}>Assessment</th>
                    <th style={{ width: "20%", padding: "6px", border: "1px solid #ddd", textAlign: "left" }}>Completed</th>
                    <th style={{ width: "40%", padding: "6px", border: "1px solid #ddd", textAlign: "left" }}>Findings</th>
                  </tr>
                </thead>
                <tbody>
                  {screeners.map(({ label, completedKey, findingsKey }) => (
                    <tr key={completedKey}>
                      <td style={{ padding: "6px", border: "1px solid #ddd" }}>{label}</td>
                      <td style={{ padding: "6px", border: "1px solid #ddd" }}>{completedStatus(nurse[completedKey])}</td>
                      <td style={{ padding: "6px", border: "1px solid #ddd" }}>{nurse[findingsKey] || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </td>
          </tr>

          {/* Education Topics Covered */}
          <Section title="Education Topics Covered" />
          <Row label="Parent-Child Interaction" value={nurse.educationParentChildInteraction} />
          <Row label="Maternal Well-being" value={nurse.educationMaternalWellbeing} />
          <Row label="Health & Development" value={nurse.educationHealthDevelopment} />
          <Row label="Safety & Environment" value={nurse.educationSafetyEnvironment} />
          <Row label="Family & Community Support" value={nurse.educationFamilyCommunitySupport} />

          {/* Referrals Made */}
          <Section title="Referrals Made" />
          <tr>
            <td colSpan={2} style={{ padding: 0, border: "1px solid #ddd" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  <tr>
                    <td style={{ width: "50%", verticalAlign: "top", padding: "8px" }}>
                      <div style={{ fontWeight: 700, marginBottom: "6px" }}>External Referrals</div>
                      <div>
                        {orderedSelected(nurse.referrals, REFERRALS_ORDER)
                          .filter((i) => i !== "Other")
                          .map((i) => (
                            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ color: "#0d7f6a", fontWeight: 700 }}>✓</span>
                              <span>{i}</span>
                            </div>
                          ))}
                      </div>
                      {Array.isArray(nurse.referrals) &&
                        nurse.referrals.includes("Other") &&
                        nurse.referralsOther?.trim() && (
                          <div style={{ marginTop: 6 }}>
                            <strong>Other:</strong> {nurse.referralsOther.trim()}
                          </div>
                        )}
                    </td>
                    <td style={{ width: "50%", verticalAlign: "top", padding: "8px", borderLeft: "1px solid #ddd" }}>
                      <div style={{ fontWeight: 700, marginBottom: "6px" }}>Hooyo Program Referrals</div>
                      <div>
                        {orderedSelected(nurse.hooyoReferral, HOOYO_ORDER).map((i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ color: "#0d7f6a", fontWeight: 700 }}>✓</span>
                            <span>{i}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>

          {/* Referral Notes — Full width, normal row */}
          <Row label="Referral Notes / Follow-up Actions" value={nurse.referralNotes} />

          {/* Goals & Action Steps */}
          <Section title="Goals & Action Steps" />
          <Row label="Client-Identified Goal(s)" value={nurse.clientGoals || "—"} />
          <Row label="Next Steps / Nurse Recommendations" value={nurse.nurseRecommendations} />
          <Row label="Follow-up Plan" value={nurse.followupPlan} />
          <Row label="Next Visit Date (tentative)" value={fmt(nurse.nextVisitDate)} />

          {/* Nurse Notes / Narrative Summary */}
          <Section title="Nurse Notes / Narrative Summary" />
          <Row label="Nurse Notes" value={nurse.nurseNotes} />
          <Row label="Nurse Name" value={nurse.nurseName} />
          <Row label="Credential" value={nurse.credential} />
          <Row label="Date of Entry" value={fmt(nurse.dateOfEntry)} />
          <Row label="Nurse Signature" value={signText} />
        </tbody>
      </table>
    </Paper>
  );
};

export default NurseVisitPreview;